/**
 * useWlInternalTenant — Hook do painel WL para ler e definir (uma única vez)
 * o tenant interno do WL.
 *
 * - `query` busca o estado atual via RPC `wl_get_internal_tenant`.
 * - `setInternalTenant` chama `wl_set_internal_tenant` em modo:
 *     - 'elect'     → promove tenant customer existente (p_tenant_id)
 *     - 'provision' → cria tenant novo (p_name + p_slug)
 *
 * Operação é one-shot: após salva, qualquer tentativa de nova escrita é bloqueada
 * tanto no banco (índice único parcial + triggers) quanto na RPC (validação).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useWlInternalTenant');

export interface WlInternalTenantRow {
  is_configured: boolean;
  internal_tenant_id: string | null;
  tenant_name: string | null;
  tenant_slug: string | null;
  tenant_status: string | null;
  set_by: string | null;
  set_by_name: string | null;
  set_at: string | null;
}

export type SetInternalTenantArgs =
  | { mode: 'elect'; tenant_id: string }
  | { mode: 'provision'; name: string; slug: string };

export function useWlInternalTenant(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  // ---- Leitura -----------------------------------------------------------
  const query = useQuery({
    queryKey: ['wl-internal-tenant'],
    queryFn: async (): Promise<WlInternalTenantRow | null> => {
      const { data, error } = await supabase.rpc('wl_get_internal_tenant');
      if (error) {
        logger.error('wl_get_internal_tenant falhou', { error });
        throw error;
      }
      const row = Array.isArray(data) ? data[0] : data;
      return (row as WlInternalTenantRow) ?? null;
    },
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });

  // ---- Escrita única -----------------------------------------------------
  const mutation = useMutation({
    mutationFn: async (args: SetInternalTenantArgs): Promise<string> => {
      const payload =
        args.mode === 'elect'
          ? { p_mode: 'elect', p_tenant_id: args.tenant_id, p_name: null, p_slug: null }
          : { p_mode: 'provision', p_tenant_id: null, p_name: args.name, p_slug: args.slug };

      const { data, error } = await supabase.rpc('wl_set_internal_tenant', payload as any);
      if (error) {
        logger.error('wl_set_internal_tenant falhou', { error, args });
        throw error;
      }
      return data as string;
    },
    onSuccess: () => {
      // Invalida listas/KPIs que dependem da classificação de tenants
      queryClient.invalidateQueries({ queryKey: ['wl-internal-tenant'] });
      queryClient.invalidateQueries({ queryKey: ['wl-tenants'] });
      queryClient.invalidateQueries({ queryKey: ['wl-tenant-kpis'] });
      queryClient.invalidateQueries({ queryKey: ['wl-tenant-users'] });
      queryClient.invalidateQueries({ queryKey: ['wl-provider-instances'] });
      toast.success('Tenant interno definido', {
        description:
          'Configuração salva com sucesso. Esta operação é definitiva e não poderá ser alterada.',
      });
    },
    onError: (err: any) => {
      toast.error('Não foi possível definir o tenant interno', {
        description: err?.message || 'Erro inesperado',
      });
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    setInternalTenant: mutation.mutateAsync,
    isSaving: mutation.isPending,
  };
}
